import { fetchTileSetFile } from "./tiledSource";
import { pickPreview, type TiledDrawingSource } from "./tileMath";

/**
 * Fetch and decode the preview tier for a consumer that caps its bitmap at
 * `maxSize` (see `pickPreview`). Decodes at the preview's natural size;
 * only the fallback case (smallest tier still over the cap) resizes down.
 * Dimensions come from the manifest — no image is decoded just to measure it.
 */
export async function fetchPreviewBitmap(source: TiledDrawingSource, maxSize: number): Promise<ImageBitmap> {
    const preview = pickPreview(source, maxSize);
    if (!preview) {
        throw new Error("Tile set has no previews");
    }

    const response = await fetchTileSetFile(source, preview.path);
    if (!response.ok) {
        throw new Error(`Failed to fetch drawing preview: HTTP ${response.status} for ${preview.path}`);
    }
    const blob = await response.blob();

    const longest = Math.max(preview.width, preview.height);
    if (longest <= maxSize) {
        return createImageBitmap(blob);
    }
    const scale = maxSize / longest;
    return createImageBitmap(blob, {
        resizeWidth: Math.max(1, Math.round(preview.width * scale)),
        resizeHeight: Math.max(1, Math.round(preview.height * scale)),
        resizeQuality: "high",
    });
}
